import * as React from "react";
import {
    DateType,
    IAvailabilitySchedule,
    IClinician,
    OfficeRequestStatus,
    RecurrenceRule,
} from "intake_shared/src/types";
import store from "../../../../store";
import { getDayTimeFromScheduleDate } from "../../../../utils/utils";
import moment from "moment";
import { useToasts } from "react-toast-notifications";
import { Dialog } from "../../bits/Dialog/Dialog";
import { TextArea } from "../../bits/TextArea/TextArea";
import styles from "./DeleteScheduleDialog.module.scss";
import { CacheContext } from "../../../../contexts/CacheContext";
import { getIntakeEmailSignature } from "../../../../utils/intakeEmailSignature";

interface DeleteScheduleDialogProps {
    schedule: IAvailabilitySchedule | null;
    setScheduleToEdit: (
        value: React.SetStateAction<IAvailabilitySchedule | null>
    ) => void;
    loggedInClinician: IClinician | undefined;
    setShowDeleteScheduleDialog: (value: boolean) => void;
    showDeleteScheduleDialog: boolean;
    setRefreshKey?: (value: React.SetStateAction<number>) => void;
}

export default function DeleteScheduleDialog({
    schedule,
    setScheduleToEdit,
    loggedInClinician,
    setShowDeleteScheduleDialog,
    showDeleteScheduleDialog,
    setRefreshKey,
}: DeleteScheduleDialogProps) {
    const { addToast } = useToasts();
    const olc = React.useContext(CacheContext);
    const intakeSignature = getIntakeEmailSignature(olc);
    const [reasonForDelete, setReasonForDelete] = React.useState("");
    if (!schedule) {
        return <></>;
    }
    const startStr = getDayTimeFromScheduleDate(schedule.start, "h:mma");
    const endStr = getDayTimeFromScheduleDate(schedule.end, "h:mma");
    const handleCancel = () => setShowDeleteScheduleDialog(false);

    const handleConfirm = async () => {
        const ensureDate = (value: DateType | undefined): Date | string => {
            if (!value || value === "") {
                return "";
            }
            return typeof value === "string" ? new Date(value) : value;
        };
        const success = await store.createClinicianOfficeWindowRequests({
            clinician_request_data: {
                ...schedule,
                start_date: ensureDate(schedule.start) as Date,
                end_date: ensureDate(schedule.end) as Date,
                change_end_time_reason: "",
                location: schedule.location || "",
                office: schedule.office || "",
                day: moment(schedule.start).format("dddd"),
                clinician_name: loggedInClinician?.fullname as string,
                requester: loggedInClinician?.email as string,
                category: "delete_office_window_list",
                request_date: new Date(),
                status: OfficeRequestStatus.PENDING,
                recurrence: schedule.recurrenceRule || ("" as RecurrenceRule),
                assigned_to: "",
                ending_date: schedule.ending_date || "",
                waitlist_count: 0,
                request_list_count: 0,
                delivery_option: schedule.serviceDeliveryOption || "",
                office_window_request_id: schedule.id || "",
                reason_for_delete: reasonForDelete,
            },
            intakeSignature,
        });

        if (!success) {
            addToast("Failed to send delete request", {
                appearance: "error",
            });
        } else {
            addToast("Request to delete office window successfully sent", {
                appearance: "success",
            });
            setShowDeleteScheduleDialog(false);
            setScheduleToEdit(null);
            if (setRefreshKey) {
                setRefreshKey((prevKey) => prevKey + 1);
            }
        }
    };

    return (
        <Dialog
            open={showDeleteScheduleDialog}
            onClose={handleCancel}
            onSave={handleConfirm}
            saveText="Submit"
            isSubmitBtnDisabled={!reasonForDelete}
            title={`Delete ${schedule.title}: ${startStr} - ${endStr} Window`}
        >
            <form className="v2-form">
                <TextArea
                    isRequired
                    value={reasonForDelete}
                    onChange={(e) => setReasonForDelete(e.target.value)}
                    label="Please provide a reason why you want to delete this window?"
                    className={styles.reasonForDelete}
                />
            </form>
        </Dialog>
    );
}
